import { Box, Stack, Typography } from "@mui/material";
import { IObjectRideSegment } from "app/data/types";
import {
  getMixedRideModeTotals,
  getSegmentColor,
  getSegmentDistanceKm,
  getSegmentDurationMinutes
} from "app/data/helpers/mixedRideSegmentHelpers";
import { useTranslation } from "react-i18next";

interface Props {
  segments: IObjectRideSegment[];
  fallbackPrivateMileage?: number | null;
}

const MixedRideStackedBar = ({ segments, fallbackPrivateMileage }: Props) => {
  const { t } = useTranslation();
  const measuredPrivateKm = getMixedRideModeTotals(segments).privateKm;
  const totals = getMixedRideModeTotals(segments, fallbackPrivateMileage);
  const hasHiddenPrivate = segments.some((segment) => segment.isPrivacyHidden);
  const usedPrivateFallback = measuredPrivateKm <= 0 && totals.privateKm > 0;
  const showPrivateKm =
    totals.privateKm > 0 && (!hasHiddenPrivate || usedPrivateFallback);
  const hidePercents = hasHiddenPrivate && !showPrivateKm;
  const hiddenDuration = segments.reduce((sum, segment) => {
    if (!segment.isPrivacyHidden) return sum;
    return sum + Math.max(getSegmentDurationMinutes(segment), 1);
  }, 0);

  return (
    <Stack spacing={1.25} mt={2.5} data-testid="mixed-ride-stacked-bar">
      <Stack direction="row" spacing={2.5} flexWrap="wrap">
        <Stack direction="row" alignItems="center" spacing={0.75}>
          <Box
            width={8}
            height={8}
            borderRadius="2px"
            bgcolor={getSegmentColor("BUSINESS")}
          />
          <Typography fontSize={13} fontWeight={500} color="#1D1F2B">
            {hidePercents
              ? t("typography.mixedRideBusinessLegendKm", {
                  km: totals.businessKm
                })
              : t("typography.mixedRideBusinessLegend", {
                  km: totals.businessKm,
                  percent: totals.businessPercent
                })}
          </Typography>
        </Stack>
        <Stack direction="row" alignItems="center" spacing={0.75}>
          <Box
            width={8}
            height={8}
            borderRadius="2px"
            bgcolor={getSegmentColor("PRIVATE")}
          />
          <Typography fontSize={13} fontWeight={500} color="#1D1F2B">
            {showPrivateKm
              ? t("typography.mixedRidePrivateLegend", {
                  km: totals.privateKm,
                  percent: totals.privatePercent
                })
              : t("typography.mixedRidePrivateHiddenLegend")}
          </Typography>
        </Stack>
      </Stack>
      <Stack
        direction="row"
        height={8}
        borderRadius="8px"
        overflow="hidden"
        bgcolor="#EFF4FF"
      >
        {segments.map((segment) => {
          const distance = getSegmentDistanceKm(segment);
          const hiddenWeight =
            usedPrivateFallback && hiddenDuration > 0
              ? (totals.privateKm *
                  Math.max(getSegmentDurationMinutes(segment), 1)) /
                hiddenDuration
              : Math.max(getSegmentDurationMinutes(segment), 1);
          const weight = segment.isPrivacyHidden
            ? hiddenWeight
            : Math.max(distance, 0.1);
          return (
            <Box
              key={segment.uniqueId}
              flexGrow={weight}
              bgcolor={getSegmentColor(segment.mode)}
              minWidth={segment.isPrivacyHidden || distance > 0 ? 4 : 0}
            />
          );
        })}
      </Stack>
    </Stack>
  );
};

export default MixedRideStackedBar;
